import {AfterViewInit, Component, EventEmitter, Input, Output} from '@angular/core';
import {JSONSchema7} from 'json-schema';
import {firstValueFrom} from 'rxjs';
import {
    CpmAEAllPluginSpecApiSchema,
    CpmAEPluginFunctionApiSchema,
    CpmAEPluginSpecApiSchema
} from '../../../../gen/cpm-ae-api-ts/src/schemas/ae-global/cpmae-plugins.api-schemas';
import {
    ECpmCObjectStoreEntryAttributesProxyApiEnum,
    ECpmCObjectStoreEntryProxyTypeApiEnum
} from '../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-object-store-entry-proxy.api-enums';
import {ECpmCReferenceExpressionSourceTypeApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/c-global/cpmc-reference-expression.api-enums';
import {CpmCObjectStoreEntryProxyApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-object-store-entry-proxy.api-model';
import {CpmCObjectStoreEntryProxyCreateApiSchema} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-object-store-entry-proxy.api-schemas';
import {CpmCTaskInputOutputMappingApiSchema} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-task.api-schemas';
import {CpmCObjectStoreEntryProxyApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-object-store-entry-proxy.api-service';
import {IBaseComponent} from '../../../../sdk/src/components/ibase/ibase.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {ConfirmationModalConfig} from '../../../../sdk/src/modals/confirm-modal/confirm-modal.component';
import {
    JsonSchemaFormModalComponent,
    JsonSchemaFormModalResult
} from '../../../../sdk/src/modals/json-schema-form-modal/json-schema-form-modal.component';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {JsonUtils} from '../../../../sdk/src/utils/json.utils';
import {JobsSpecificationCaching} from '../../caching/jobs-specification.caching';
import {
    NewEditObjectStoreEntryModalComponent,
    NewEditObjectStoreEntryModalConfig
} from '../../modals/new-edit-object-store-entry-modal/new-edit-object-store-entry-modal.component';
import {
    ReferenceExpressionModalComponent,
    ReferenceExpressionModalResult
} from '../../modals/reference-expression-modal/reference-expression-modal.component';
import {
    EObjectStoreEntriesLevel,
    ObjectStoreEntriesTransientCache
} from '../../transient-cache/object-store-entries.transient-cache';
import {ObjectStoreEntryUtil} from '../../utils/object-store-entry.util';
import {ReferenceExpressionInstance, ReferenceExpressionUtils} from '../../utils/reference-expression.utils';
import {TaskUtils} from '../../utils/task.utils';

class ActionJobsFunctionIoProperty {
    constructor(
        public name: string,
        public schema: JSONSchema7,
        public required: boolean,
        public valid: boolean,
        public objectStoreEntryVtype: ECpmCObjectStoreEntryProxyTypeApiEnum,
        public objectStoreEntrySchemaKey: string | undefined,
        public order: number | undefined
    ) {}
}

/**
 * Action Jobs Function IO Editor Change Event
 */
export interface ActionJobsFunctionIoEditorChangeEvent {
    side: 'input' | 'output';
    confirmationHandled: boolean;
}

/**
 * Action Jobs Function IO Editor
 */
@Component({
    selector: 'app-action-jobs-function-io-editor',
    templateUrl: './action-jobs-function-io-editor.component.html',
    styleUrl: './action-jobs-function-io-editor.component.scss',
    imports: [PrimeNgImportsModule]
})
export class ActionJobsFunctionIoEditor extends IBaseComponent implements AfterViewInit {
    // ===============================================================
    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected _specificationAppDetails?: CpmAEAllPluginSpecApiSchema;
    protected _plugin?: CpmAEPluginSpecApiSchema;
    protected _pluginFunction?: CpmAEPluginFunctionApiSchema;
    protected _inputSchema?: JSONSchema7;
    protected _properties: ActionJobsFunctionIoProperty[] = [];
    protected _ngModels: Record<string, {content: string; complexValue: boolean}> = {};
    protected _isValid = false;

    // ===============================================================
    // ===============================================================

    @Input()
    public overrideHeader?: string;

    @Input()
    public taskLabel?: string;

    @Input()
    public side: 'input' | 'output' = 'input';

    public get plugin(): CpmAEPluginSpecApiSchema | undefined {
        return this._plugin;
    }
    @Input()
    public set plugin(value: CpmAEPluginSpecApiSchema | undefined) {
        if (value == this._plugin) return;

        this._plugin = value;
    }

    public get pluginFunction(): CpmAEPluginFunctionApiSchema | undefined {
        return this._pluginFunction;
    }
    @Input()
    public set pluginFunction(value: CpmAEPluginFunctionApiSchema | undefined) {
        if (value == this._pluginFunction) return;

        this._pluginFunction = value;
        void this.onPluginFunctionChanged();
    }

    @Input()
    public iosMapping?: Record<string, CpmCTaskInputOutputMappingApiSchema>;

    @Input()
    public readOnly = false;

    @Input()
    public noFrame = false;
    @Input()
    public noSeparator = false;

    @Input()
    public objectStoreEntriesTransientCache?: ObjectStoreEntriesTransientCache;

    public get isValid() {
        return this._isValid;
    }

    @Input()
    public inputChangeConfirmation?: ConfirmationModalConfig;

    @Input()
    public persistMappingsAsync?: (
        nextMappings: Record<string, CpmCTaskInputOutputMappingApiSchema>,
        event: ActionJobsFunctionIoEditorChangeEvent
    ) => Promise<boolean | void>;

    @Output()
    public validityChangedEvent = new EventEmitter<boolean>();

    @Output()
    public changedEvent = new EventEmitter<ActionJobsFunctionIoEditorChangeEvent>();

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}
    /**
     * After view init
     */
    public ngAfterViewInit(): void {
        this.refreshNgContents();
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the plugin function changed
     */
    protected async onPluginFunctionChanged() {
        if (!this._specificationAppDetails) {
            this.loadingStr = 'Loading active specification';
            this.showLoading = true;
            await this.asyncCritical(async () => {
                // Retrieve the action‑job specification from the cache.
                this._specificationAppDetails = (await firstValueFrom(
                    JobsSpecificationCaching.getExecutorActiveSpecificationAppDetails('action')
                )) as unknown as CpmAEAllPluginSpecApiSchema;

                if (!this._specificationAppDetails) {
                    throw new Error("No active specification found for jobsType 'Action'");
                }
            });
            this.showLoading = false;
        }

        this._properties.splice(0, this._properties.length);

        const src = this.side == 'input' ? this._pluginFunction?.input_schema : this._pluginFunction?.output_schema;
        if (!src) return;

        this._inputSchema = {
            ...(src as JSONSchema7),
            definitions: this._specificationAppDetails!.type_definitions as Record<string, JSONSchema7>
        };

        if (this._inputSchema.properties) {
            Object.keys(this._inputSchema.properties).forEach((k) => {
                let subSchema = this._inputSchema!.properties![k] as JSONSchema7;
                const objectStoreType = ObjectStoreEntryUtil.resolveTypeFromPluginFunctionJsonSchemaProperty(
                    subSchema!,
                    this._inputSchema!
                );

                if (subSchema.anyOf) {
                    if (subSchema.anyOf.length == 2 && subSchema.anyOf.some((x) => (x as JSONSchema7).type == 'null')) {
                        subSchema = {
                            ...subSchema,
                            ...(subSchema.anyOf.find((x) => (x as JSONSchema7).type != 'null') as JSONSchema7)
                        };
                        delete subSchema.anyOf;
                    } else throw new Error('Unsupported anyOf with more than 2 types');
                }

                let required = false;
                if (
                    this.side == 'input' &&
                    this._inputSchema!.required &&
                    this._inputSchema!.required.indexOf(k) !== -1
                )
                    required = true;

                this._properties.push({
                    name: k,
                    schema: subSchema,
                    required: required,
                    valid: false,
                    objectStoreEntryVtype: objectStoreType.vtype,
                    objectStoreEntrySchemaKey: objectStoreType.schemaKey,
                    order: (subSchema as Record<string, unknown>)?.['x-order'] as number | undefined
                });
            });

            this._properties.sort((a, b) => {
                if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
                else if (a.order !== undefined) return -1;
                else if (b.order !== undefined) return 1;

                return a.name < b.name ? -1 : 1;
            });
        }

        this.refreshNgContents();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Notify that the mapping has changed externally
     */
    public notifyMappingChangedExternally(): void {
        this.refreshNgContents();
    }

    /**
     * Refresh properties displays
     */
    protected refreshNgContents() {
        this._properties.forEach((property) => {
            let content = '';
            let complexValue = true;

            if (this.iosMapping?.[property.name]?.value_serialized) {
                const serializedValue = this.iosMapping[property.name].value_serialized!;

                const previewResult = this.generateSerializedValuePreview(serializedValue);
                content = previewResult.preview;
                complexValue = previewResult.isComplex;
            } else if (this.iosMapping?.[property.name]?.reference_expression) {
                content = this.iosMapping[property.name].reference_expression!;
                complexValue = true;

                const internalExpression = this.resolveInternalReferenceExpression(content);
                if (internalExpression.objectStoreEntry) {
                    const previewResult = this.generateSerializedValuePreview(
                        internalExpression.objectStoreEntry.value
                    );
                    content = previewResult.preview;
                    complexValue = previewResult.isComplex;
                }
            }

            this._ngModels[property.name] = {
                content: content,
                complexValue: complexValue
            };
        });

        this.onValidityChanged();
    }
    /**
     * When the validity changed
     */
    protected onValidityChanged() {
        this._properties.forEach((prop) => {
            if (prop.required) {
                if (
                    this.iosMapping?.[prop.name]?.value_serialized ||
                    this.iosMapping?.[prop.name]?.reference_expression
                )
                    prop.valid = true;
                else prop.valid = false;
            } else prop.valid = true;
        });

        this._isValid = !this._properties.some((x) => !x.valid);
        this.validityChangedEvent.next(this._isValid);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Edit as reference expression
     * @param property Property to edit
     */
    protected editReferenceExpression(property: ActionJobsFunctionIoProperty) {
        const existingReferenceExpression = this.iosMapping?.[property.name]?.reference_expression;
        const confirmationHandled = this.side === 'input' && !!this.inputChangeConfirmation;

        this.globalsService.dialogService
            .open(
                ReferenceExpressionModalComponent,
                ReferenceExpressionModalComponent.getDialogServiceParameters({
                    title: this.taskLabel ? `${this.taskLabel} - ${property.name}` : property.name,
                    schema: property.schema,
                    rootSchema: this._inputSchema!,
                    readOnly: this.readOnly,
                    allowedSourceTypes: [ECpmCReferenceExpressionSourceTypeApiEnum.OBJECT_STORE],
                    sourceIdentifiersGenerator: (x) =>
                        TaskUtils.referenceExpressionSourceIdentifiersGenerator(x, this.side == 'input'),
                    attributeNamesGenerator: (x) =>
                        TaskUtils.referenceExpressionAttributeNamesGenerator(
                            x,
                            this.objectStoreEntriesTransientCache,
                            property.objectStoreEntryVtype,
                            property.objectStoreEntrySchemaKey
                        ),
                    objectStoreUUIDGenerator: (linkObjectType) => {
                        switch (linkObjectType) {
                            case EObjectStoreEntriesLevel.CUSTOMER:
                                return this.objectStoreEntriesTransientCache?.customerUUID;
                            case EObjectStoreEntriesLevel.ORGANIZATION:
                                return this.objectStoreEntriesTransientCache?.organizationUUID;
                            case EObjectStoreEntriesLevel.PROJECT:
                                return this.objectStoreEntriesTransientCache?.projectUUID;
                            case EObjectStoreEntriesLevel.SERVICE_REQUEST:
                                return this.objectStoreEntriesTransientCache?.serviceRequestUUID;
                            default:
                                return undefined;
                        }
                    },
                    expression: existingReferenceExpression,
                    defaultSourceType: ECpmCReferenceExpressionSourceTypeApiEnum.OBJECT_STORE,
                    defaultSourceIdentifier: 'service_request',
                    defaultKeyLabel: this.taskLabel ? `${this.taskLabel} - ${property.name}` : property.name,
                    continueConfirmation: confirmationHandled ? this.inputChangeConfirmation : undefined,
                    continueAsync: this.persistMappingsAsync
                        ? async (expression: string) => {
                              const nextMappings = this.cloneMappingsWithValue(property.name, {
                                  reference_expression: expression
                              });

                              return await this.persistMappingsAsync!(nextMappings, {
                                  side: this.side,
                                  confirmationHandled
                              });
                          }
                        : undefined,
                    defaultKeyCreator: async (key) => {
                        const linkObjectUUID = this.objectStoreEntriesTransientCache?.serviceRequestUUID;
                        if (!linkObjectUUID) throw new Error('No service request Object Store is available');

                        const payload: CpmCObjectStoreEntryProxyCreateApiSchema = {
                            vtype: property.objectStoreEntryVtype,
                            schema: property.objectStoreEntrySchemaKey,
                            value: null,
                            overwrite_existing: false
                        };

                        await firstValueFrom(
                            CpmCObjectStoreEntryProxyApiService.setObjectStoreEntryProxy(
                                EObjectStoreEntriesLevel.SERVICE_REQUEST,
                                linkObjectUUID,
                                key,
                                payload
                            )
                        );

                        await this.objectStoreEntriesTransientCache!.updateEntriesForLevel(
                            EObjectStoreEntriesLevel.SERVICE_REQUEST
                        );
                    }
                })
            )
            .onClose.subscribe((res: ReferenceExpressionModalResult) => {
                if (!res.expression || this.readOnly) return;

                const changed = this.iosMapping![property.name]?.reference_expression != res.expression;

                this.iosMapping![property.name] = {
                    reference_expression: res.expression
                };
                this.refreshNgContents();

                if (changed && !this.persistMappingsAsync) {
                    this.changedEvent.emit({
                        side: this.side,
                        confirmationHandled
                    });
                }
            });
    }

    /**
     * Edit as user value
     * @param property Property to edit
     */
    protected editUserValue(property: ActionJobsFunctionIoProperty) {
        let existingDataSerialized = this.iosMapping?.[property.name]?.value_serialized;
        let existingData: unknown = undefined;

        const internalExpression = this.resolveInternalReferenceExpression(
            this.iosMapping?.[property.name]?.reference_expression
        );
        if (internalExpression.objectStoreEntry) {
            existingDataSerialized = internalExpression.objectStoreEntry.value;
        }

        if (existingDataSerialized !== undefined && existingDataSerialized !== null) {
            existingData = JsonUtils.deserializeValue(existingDataSerialized);
        }

        const confirmationHandled = this.side === 'input' && !!this.inputChangeConfirmation;

        this.globalsService.dialogService
            .open(
                JsonSchemaFormModalComponent,
                JsonSchemaFormModalComponent.getDialogServiceParameters({
                    title: 'Input ' + property.name,
                    schema: property.schema,
                    rootSchema: this._inputSchema,
                    data: existingData,
                    readOnly: this.readOnly,
                    validateConfirmation: confirmationHandled ? this.inputChangeConfirmation : undefined,
                    validateAsync: this.persistMappingsAsync
                        ? async (data: unknown) => {
                              const serialized = JsonUtils.serializeValue(data);
                              const nextMappings = this.cloneMappingsWithValue(property.name, {
                                  value_serialized: serialized
                              });

                              return await this.persistMappingsAsync!(nextMappings, {
                                  side: this.side,
                                  confirmationHandled
                              });
                          }
                        : undefined
                })
            )
            .onClose.subscribe((res: JsonSchemaFormModalResult) => {
                if (!res.validated || this.readOnly) return;

                const serialized = JsonUtils.serializeValue(res.data);
                const changed = this.iosMapping![property.name]?.value_serialized != serialized;

                this.iosMapping![property.name] = {
                    value_serialized: serialized
                };

                this.refreshNgContents();

                if (changed && !this.persistMappingsAsync) {
                    this.changedEvent.emit({
                        side: this.side,
                        confirmationHandled
                    });
                }
            });
    }

    /**
     * Display the current value of the specified property.
     * @param property Property to display
     */
    displayCurrentValue(property: ActionJobsFunctionIoProperty) {
        const referenceExpression = this.iosMapping?.[property.name]?.reference_expression;
        if (!referenceExpression) return;

        const expressions = ReferenceExpressionUtils.parseExpressions(referenceExpression, true);
        if (
            expressions.length !== 1 ||
            expressions[0].start !== 0 ||
            expressions[0].end !== referenceExpression.length ||
            expressions[0].sourceType !== ECpmCReferenceExpressionSourceTypeApiEnum.OBJECT_STORE
        )
            return;

        const objectStoreEntry = this.objectStoreEntriesTransientCache?.getDefinedOrInheritedEntry(
            expressions[0].attributeName
        );
        if (!objectStoreEntry) return;

        const linkObjectType = this.objectStoreEntriesTransientCache?.getEntryLevel(objectStoreEntry);
        if (!linkObjectType) return;

        let linkObjectUUID: string | undefined;

        switch (linkObjectType) {
            case EObjectStoreEntriesLevel.CUSTOMER:
                linkObjectUUID = this.objectStoreEntriesTransientCache?.customerUUID;
                break;
            case EObjectStoreEntriesLevel.ORGANIZATION:
                linkObjectUUID = this.objectStoreEntriesTransientCache?.organizationUUID;
                break;
            case EObjectStoreEntriesLevel.PROJECT:
                linkObjectUUID = this.objectStoreEntriesTransientCache?.projectUUID;
                break;
            case EObjectStoreEntriesLevel.SERVICE_REQUEST:
                linkObjectUUID = this.objectStoreEntriesTransientCache?.serviceRequestUUID;
                break;
        }

        if (!linkObjectUUID) return;

        this.globalsService.dialogService.open(
            NewEditObjectStoreEntryModalComponent,
            NewEditObjectStoreEntryModalComponent.getDialogServiceParameters({
                dialogOpenMode: EDialogOpenMode.READ_ONLY,
                extended: {
                    linkObjectType,
                    linkObjectUUID,
                    entryKey: expressions[0].attributeName,
                    forceExistingRefresh: true
                } as NewEditObjectStoreEntryModalConfig
            })
        );
    }

    // ===============================================================
    // ===============================================================

    private resolveInternalReferenceExpression(expressionStr?: string | null): {
        result: boolean;
        parsedExpression?: ReferenceExpressionInstance;
        objectStoreEntry?: CpmCObjectStoreEntryProxyApiModel;
    } {
        if (!expressionStr) return {result: false};

        const expressions = ReferenceExpressionUtils.parseExpressions(expressionStr, true);
        if (expressions.length != 1) return {result: false};

        const objectStoreEntry = this.objectStoreEntriesTransientCache?.getEntryForLevel(
            expressions[0].sourceIdentifier as EObjectStoreEntriesLevel,
            expressions[0].attributeName
        );

        if (
            !objectStoreEntry ||
            (objectStoreEntry.attributes & ECpmCObjectStoreEntryAttributesProxyApiEnum.INTERNAL) == 0
        )
            return {result: false, parsedExpression: expressions[0]};

        return {result: true, parsedExpression: expressions[0], objectStoreEntry: objectStoreEntry};
    }
    /**
     * Generate a preview for a serialized value
     * @param serializedValue Serialized value
     * @returns Generate preview, complexity flag
     */
    private generateSerializedValuePreview(serializedValue: string | undefined | null): {
        preview: string;
        isComplex: boolean;
    } {
        let preview = '';
        let isComplex = false;

        if (serializedValue?.startsWith('[') || serializedValue?.startsWith('{')) {
            preview = 'Complex value (open to view details)';
            isComplex = true;
        } else if (serializedValue?.startsWith('"')) preview = serializedValue.substring(1, serializedValue.length - 1);
        else preview = serializedValue ?? '';

        return {preview: preview, isComplex: isComplex};
    }

    /**
     * Clone the current mappings with a new value for a specific property.
     * @param propertyName The name of the property to update.
     * @param mapping The new mapping for the property.
     * @returns A new record of mappings including the updated property.
     */
    private cloneMappingsWithValue(
        propertyName: string,
        mapping: CpmCTaskInputOutputMappingApiSchema
    ): Record<string, CpmCTaskInputOutputMappingApiSchema> {
        return {
            ...this.iosMapping,
            [propertyName]: mapping
        };
    }
}
